import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { Plus, Settings, Users, Calendar, ExternalLink, Trash2, Check, X, Download } from 'lucide-react';
import { toast } from 'react-toastify';
import Modal from '../../components/Modal';
import GroupForm from '../../components/GroupForm';
import DatePickerButton from '../../components/DatePickerButton';
import { useAuth } from '../../contexts/AuthContext';
import { useConfig } from '../../contexts/ConfigContext';
import { getDirectionTerms } from '../../utils/organizationHelpers';
import './IndexPage.css';
import { formatDateShortRu, formatDateRangeRu } from '../../utils/dateUtils';
import { displayGroupRole } from '../../utils/userDisplayName';
import * as XLSX from 'xlsx';

const IndexPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { config } = useConfig();
    const directionTerms = getDirectionTerms(config?.code);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [showGroupModal, setShowGroupModal] = useState(false);
    const [editingGroup, setEditingGroup] = useState(null);
    const [groupFormTab, setGroupFormTab] = useState('main');
    const initialFilters = {
        directions: [],
        regions: [],
        statuses: [],
        startDate: '',
        eventDate: '',
        manager: '',
        name: '',
        nomination: '',
        isAccepted: '',
        hasSchedule: ''
    };
    const [filters, setFilters] = useState(initialFilters);
    const [appliedFilters, setAppliedFilters] = useState(initialFilters);
    const [showFilters, setShowFilters] = useState(false);
    const [displayStartDate, setDisplayStartDate] = useState('');
    const [displayEventDate, setDisplayEventDate] = useState('');

    const formatDateForDisplay = (isoDate) => {
        if (!isoDate) return '';
        const [y, m, d] = isoDate.split('-');
        return `${d}.${m}.${y}`;
    };

    const parseDateInput = (str) => {
        if (!str || !str.trim()) return null;
        const s = str.trim().replace(/\//g, '.').replace(/-/g, '.');
        const parts = s.split('.');
        if (parts.length !== 3) return null;
        let d = parseInt(parts[0], 10), m = parseInt(parts[1], 10), y = parseInt(parts[2], 10);
        if (isNaN(d) || isNaN(m) || isNaN(y)) return null;
        if (y < 100) y += 2000;
        if (m < 1 || m > 12) return null;
        const lastDay = new Date(y, m, 0).getDate();
        if (d < 1 || d > lastDay) return null;
        return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    };

    const formatDateMask = (digits) => {
        if (!digits || digits.length === 0) return '';
        const d = digits.slice(0, 2);
        const m = digits.length > 2 ? digits.slice(2, 4) : '';
        const y = digits.length > 4 ? digits.slice(4, 8) : '';
        if (!m) return d;
        if (!y) return `${d}.${m}`;
        return `${d}.${m}.${y}`;
    };

    const applyDateMask = (value) => {
        const digits = value.replace(/\D/g, '').slice(0, 8);
        return formatDateMask(digits);
    };

    React.useEffect(() => {
        setDisplayStartDate(filters.startDate ? formatDateForDisplay(filters.startDate) : '');
    }, [filters.startDate]);

    React.useEffect(() => {
        setDisplayEventDate(filters.eventDate ? formatDateForDisplay(filters.eventDate) : '');
    }, [filters.eventDate]);

    const { data: groups, isLoading, refetch } = useQuery(
        ['userGroups', appliedFilters.eventDate],
        async () => {
            const params = appliedFilters.eventDate ? { event_date: appliedFilters.eventDate } : {};
            const response = await axios.get(`${API_BASE_URL}/groups`, {
                withCredentials: true,
                params
            });
            return response.data;
        }
    );

    const applyFilters = () => {
        setAppliedFilters({ ...filters });
    };

    const handleCreateGroup = () => {
        setEditingGroup(null);
        setGroupFormTab('main');
        setShowGroupModal(true);
    };

    const handleEditGroup = (group) => {
        setEditingGroup(group);
        setGroupFormTab('main');
        setShowGroupModal(true);
    };

    const handleGroupSuccess = () => {
        setShowGroupModal(false);
        setEditingGroup(null);
        refetch();
    };

    const handleOpenSchedule = (group) => {
        setEditingGroup(group);
        setGroupFormTab('schedule');
        setShowGroupModal(true);
    };

    const handleGroupApplications = (groupId) => {
        navigate(`/group/${groupId}/applications`);
    };

    const handleViewPublicPage = (groupId) => {
        window.open(`/g/${groupId}`, '_blank');
    };

    const handleDeleteGroup = async (groupId, groupName) => {
        if (!window.confirm(`Вы уверены, что хотите удалить группу "${groupName}"?`)) {
            return;
        }

        try {
            await axios.delete(`${API_BASE_URL}/groups/${groupId}`, {
                withCredentials: true
            });
            toast.success('Группа удалена');
            refetch();
        } catch (error) {
            console.error('Ошибка удаления группы:', error);
            toast.error(error.response?.data?.message || 'Ошибка удаления');
        }
    };

    const handleApproveGroup = async (groupId) => {
        try {
            await axios.put(
                `${API_BASE_URL}/groups/${groupId}/approve`,
                {},
                { withCredentials: true }
            );
            toast.success('Группа подтверждена');
            refetch();
        } catch (error) {
            console.error('Ошибка подтверждения группы:', error);
            toast.error(error.response?.data?.message || 'Ошибка подтверждения группы');
        }
    };

    const handleUnapproveGroup = async (groupId) => {
        try {
            await axios.put(
                `${API_BASE_URL}/groups/${groupId}/unapprove`,
                {},
                { withCredentials: true }
            );
            toast.success('Подтверждение снято');
            refetch();
        } catch (error) {
            console.error('Ошибка снятия подтверждения:', error);
            toast.error(error.response?.data?.message || 'Ошибка снятия подтверждения');
        }
    };

    const handleFilterChange = (filterName, value) => {
        setFilters(prev => ({
            ...prev,
            [filterName]: value
        }));
    };

    // Обработка множественного выбора для направлений, регионов и статусов
    const toggleArrayFilter = (filterName, value) => {
        setFilters(prev => {
            const currentArray = prev[filterName];
            const newArray = currentArray.includes(value)
                ? currentArray.filter(item => item !== value)
                : [...currentArray, value];
            return { ...prev, [filterName]: newArray };
        });
    };

    const clearFilters = () => {
        setFilters(initialFilters);
        setAppliedFilters(initialFilters);
    };

    const filteredGroups = groups?.filter(group => {
        if (appliedFilters.directions.length > 0 && !appliedFilters.directions.includes(group.direction_name)) return false;
        if (appliedFilters.regions.length > 0 && !appliedFilters.regions.includes(group.region_name)) return false;
        if (appliedFilters.statuses.length > 0 && !appliedFilters.statuses.includes(group.recruitment_status)) return false;
        if (appliedFilters.manager && !group.manager_name?.toLowerCase().includes(appliedFilters.manager.toLowerCase())) return false;
        if (appliedFilters.name && !group.name?.toLowerCase().includes(appliedFilters.name.toLowerCase())) return false;
        if (appliedFilters.nomination && !group.nomination?.toLowerCase().includes(appliedFilters.nomination.toLowerCase())) return false;
        if (appliedFilters.isAccepted !== '') {
            const isAccepted = appliedFilters.isAccepted === 'true';
            if (group.is_accepted !== isAccepted) return false;
        }
        if (appliedFilters.hasSchedule !== '') {
            const hasSchedule = appliedFilters.hasSchedule === 'true';
            if (!!group.earliest_event_date !== hasSchedule) return false;
        }
        if (appliedFilters.startDate) {
            if (!group.earliest_event_date) return false;
            const groupDate = new Date(group.earliest_event_date);
            groupDate.setHours(0, 0, 0, 0);
            const filterDate = new Date(appliedFilters.startDate + 'T00:00:00');
            if (groupDate.getTime() !== filterDate.getTime()) return false;
        }
        return true;
    }) || [];

    // Разделяем группы на две категории: с расписанием и без
    // Сортируем группы с расписанием по дате старта — от самой поздней к самой ранней
    const groupsWithSchedule = filteredGroups
        .filter(group => group.earliest_event_date)
        .sort((a, b) => {
            const dateA = new Date(a.earliest_event_date);
            const dateB = new Date(b.earliest_event_date);
            return dateB - dateA; // Сортировка от поздней даты к ранней
        });

    const groupsWithoutSchedule = filteredGroups.filter(group => !group.earliest_event_date);

    const getStatusBadge = (status) => {
        const statuses = {
            open: { label: 'Набор открыт', className: 'status-open' },
            closed: { label: 'Набор закрыт', className: 'status-closed' },
            started: { label: 'Группа запущена', className: 'status-started' },
            started_additional: { label: 'Группа запущена, донабор', className: 'status-started-additional' },
            completed: { label: 'Группа завершена', className: 'status-completed' },
            cancelled: { label: 'Группа не запустилась', className: 'status-cancelled' }
        };
        return statuses[status] || statuses.open;
    };

    const handleExportGroups = () => {
        const list = groups || [];
        if (list.length === 0) {
            toast.info('Нет групп для выгрузки');
            return;
        }
        const headers = [
            'ID', 'Название', 'Наименование', directionTerms.one, 'Регион', 'Менеджер',
            'Дата старта', 'Даты встреч', 'Статус', 'Подтверждена',
            'Цена за цикл', 'Цена за курс', 'О группе', 'Ссылка на группу', 'Доступ (роли)', 'Альтернативная форма'
        ];
        const origin = typeof window !== 'undefined' ? window.location.origin : '';
        const rows = list.map(g => {
            const datesStr = (g.schedule_events_dates || [])
                .map(ev => formatDateRangeRu(ev.start_date, ev.end_date))
                .filter(Boolean)
                .join('; ');
            const accessParts = [
                g.manager_name ? `${g.manager_name} (Менеджер)` : null,
                ...(g.access_data || []).map(a => `${a.name || ''} (${displayGroupRole(a.role) || 'Ассистент'})`)
            ].filter(Boolean);
            const accessStr = accessParts.join('; ');
            const groupLink = origin ? `${origin}/g/${g.id}` : '';
            return [
                g.id,
                g.name || '',
                g.nomination || '',
                g.direction_name || '',
                g.region_name || '',
                g.manager_name || '',
                formatDateShortRu(g.earliest_event_date),
                datesStr,
                getStatusBadge(g.recruitment_status).label,
                g.is_accepted ? 'Да' : 'Нет',
                g.price_per_cycle != null ? g.price_per_cycle : '',
                g.price_full_course != null ? g.price_full_course : '',
                (g.about || '').replace(/\r?\n/g, ' '),
                groupLink,
                accessStr,
                g.alternative_form || ''
            ];
        });
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
        XLSX.utils.book_append_sheet(wb, ws, 'Группы');
        XLSX.writeFile(wb, `группы_${new Date().toISOString().slice(0, 10)}.xlsx`);
        toast.success('Выгрузка сохранена');
    };

    if (isLoading) {
        return (
            <div className="loading">
                <div className="spinner"></div>
            </div>
        );
    }

    return (
        <div className="index-page">
            <div className="page-header">
                <div>
                    <h1>Мои группы</h1>
                    <p>Управление учебными группами</p>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button
                        className="btn btn-secondary"
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        {showFilters ? 'Скрыть фильтры' : 'Показать фильтры'}
                    </button>
                    <button
                        className="btn btn-secondary"
                        onClick={handleExportGroups}
                        disabled={!groups?.length}
                        title="Скачать таблицу со всеми группами в XLSX"
                    >
                        <Download size={18} />
                        Выгрузка
                    </button>
                    <button className="btn btn-primary" onClick={handleCreateGroup}>
                        <Plus size={18} />
                        Создать группу
                    </button>
                </div>
            </div>

            {groups?.length === 0 ? (
                <div className="empty-state">
                    <Users size={64} className="empty-icon" />
                    <h3>У вас пока нет групп</h3>
                    <p>Создайте свою первую группу, чтобы начать работу</p>
                    <button className="btn btn-primary btn-large" onClick={handleCreateGroup}>
                        <Plus size={20} />
                        Создать первую группу
                    </button>
                </div>
            ) : (
                <>
                    {/* Фильтры */}
                    {showFilters && (
                        <form
                            className="filters-section"
                            onSubmit={(e) => {
                                e.preventDefault();
                                applyFilters();
                            }}
                        >
                            <h3>Фильтры</h3>

                            {/* Первая строка: Направления, Регионы, Статусы */}
                            <div className="filters-row-main">
                                <div className="filter-group">
                                    <label>{directionTerms.many}</label>
                                    <div className="checkbox-filter-container">
                                        {groups?.reduce((acc, group) => {
                                            if (group.direction_name && !acc.includes(group.direction_name)) {
                                                acc.push(group.direction_name);
                                            }
                                            return acc;
                                        }, []).sort().map(direction => (
                                            <label key={direction} className="checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={filters.directions.includes(direction)}
                                                    onChange={() => toggleArrayFilter('directions', direction)}
                                                />
                                                <span>{direction}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                <div className="filter-group">
                                    <label>Регионы</label>
                                    <div className="checkbox-filter-container">
                                        {groups?.reduce((acc, group) => {
                                            if (group.region_name && !acc.includes(group.region_name)) {
                                                acc.push(group.region_name);
                                            }
                                            return acc;
                                        }, []).sort().map(region => (
                                            <label key={region} className="checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={filters.regions.includes(region)}
                                                    onChange={() => toggleArrayFilter('regions', region)}
                                                />
                                                <span>{region}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                <div className="filter-group">
                                    <label>Статусы</label>
                                    <div className="checkbox-filter-container">
                                        {['open', 'closed', 'started', 'started_additional', 'completed', 'cancelled'].map(status => (
                                            <label key={status} className="checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={filters.statuses.includes(status)}
                                                    onChange={() => toggleArrayFilter('statuses', status)}
                                                />
                                                <span>{getStatusBadge(status).label}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Вторая строка: остальные фильтры */}
                            <div className="filters-grid">

                                <div className="filter-group">
                                    <label>Менеджер</label>
                                    <input
                                        type="text"
                                        className="filter-input-with-border"
                                        placeholder="Поиск по менеджеру"
                                        value={filters.manager}
                                        onChange={(e) => handleFilterChange('manager', e.target.value)}
                                    />
                                </div>

                                <div className="filter-group">
                                    <label>Название группы</label>
                                    <input
                                        type="text"
                                        className="filter-input-with-border"
                                        placeholder="Поиск по названию"
                                        value={filters.name}
                                        onChange={(e) => handleFilterChange('name', e.target.value)}
                                    />
                                </div>

                                <div className="filter-group">
                                    <label>Наименование</label>
                                    <input
                                        type="text"
                                        className="filter-input-with-border"
                                        placeholder="Поиск по наименованию"
                                        value={filters.nomination}
                                        onChange={(e) => handleFilterChange('nomination', e.target.value)}
                                    />
                                </div>

                                <div className="filter-group">
                                    <label>Подтверждение</label>
                                    <select
                                        value={filters.isAccepted}
                                        onChange={(e) => handleFilterChange('isAccepted', e.target.value)}
                                    >
                                        <option value="">Все</option>
                                        <option value="true">Подтвержденные</option>
                                        <option value="false">Не подтвержденные</option>
                                    </select>
                                </div>

                                <div className="filter-group">
                                    <label>Расписание</label>
                                    <select
                                        value={filters.hasSchedule}
                                        onChange={(e) => handleFilterChange('hasSchedule', e.target.value)}
                                    >
                                        <option value="">Все</option>
                                        <option value="true">С расписанием</option>
                                        <option value="false">Без расписания</option>
                                    </select>
                                </div>

                                <div className="filter-group">
                                    <label>Дата старта</label>
                                    <div className="date-field-wrapper">
                                        <div className="date-field-input-wrap">
                                            <input
                                                type="text"
                                                className="date-field-text"
                                                value={displayStartDate}
                                                onChange={(e) => setDisplayStartDate(applyDateMask(e.target.value))}
                                                onBlur={() => {
                                                    const parsed = parseDateInput(displayStartDate);
                                                    if (parsed) {
                                                        handleFilterChange('startDate', parsed);
                                                        setDisplayStartDate(formatDateForDisplay(parsed));
                                                    } else if (displayStartDate.trim() === '') {
                                                        handleFilterChange('startDate', '');
                                                    }
                                                }}
                                                title="Введите дату (дд.мм.гггг) или нажмите на календарь"
                                            />
                                        </div>
                                        <div className="date-field-trigger-wrap">
                                            <DatePickerButton
                                                value={filters.startDate}
                                                label="Выбрать дату старта"
                                                onChange={(iso) => {
                                                    handleFilterChange('startDate', iso);
                                                    setDisplayStartDate(formatDateForDisplay(iso));
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="filter-group">
                                    <label>Дата встречи</label>
                                    <div className="date-field-wrapper">
                                        <div className="date-field-input-wrap">
                                            <input
                                                type="text"
                                                className="date-field-text"
                                                value={displayEventDate}
                                                onChange={(e) => setDisplayEventDate(applyDateMask(e.target.value))}
                                                onBlur={() => {
                                                    const parsed = parseDateInput(displayEventDate);
                                                    if (parsed) {
                                                        handleFilterChange('eventDate', parsed);
                                                        setDisplayEventDate(formatDateForDisplay(parsed));
                                                    } else if (displayEventDate.trim() === '') {
                                                        handleFilterChange('eventDate', '');
                                                    }
                                                }}
                                                title="Введите дату (дд.мм.гггг) или нажмите на календарь"
                                            />
                                        </div>
                                        <div className="date-field-trigger-wrap">
                                            <DatePickerButton
                                                value={filters.eventDate}
                                                label="Выбрать дату встречи"
                                                onChange={(iso) => {
                                                    handleFilterChange('eventDate', iso);
                                                    setDisplayEventDate(formatDateForDisplay(iso));
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <span className="filter-hint">Для поиска групп по любой дате</span>
                                </div>

                                <div className="filter-group filter-action-item">
                                    <label className="filter-action-label">&nbsp;</label>
                                    <button type="submit" className="btn btn-primary">
                                        Применить фильтры
                                    </button>
                                </div>
                                <div className="filter-group filter-action-item">
                                    <label className="filter-action-label">&nbsp;</label>
                                    <button type="button" className="btn btn-secondary" onClick={clearFilters}>
                                        Очистить фильтры
                                    </button>
                                </div>
                            </div>
                        </form>
                    )}

                    {/* Группы с расписанием */}
                    {groupsWithSchedule.length > 0 && (
                        <div className="groups-section">
                            <h3 className="section-title">
                                <Calendar size={20} />
                                Группы с расписанием ({groupsWithSchedule.length})
                            </h3>
                            <table className="compact-groups-table">
                                <thead>
                                    <tr>
                                        <th>Группа</th>
                                        <th>{directionTerms.one}</th>
                                        <th>Регион</th>
                                        {user?.role === 'admin' && <th>Менеджер</th>}
                                        <th>Старт</th>
                                        <th>Стоимость</th>
                                        <th>Статус</th>
                                        <th>Действия</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {groupsWithSchedule.map(group => (
                                        <tr key={group.id} className={!group.is_accepted ? 'row-unconfirmed' : ''}>
                                            <td>
                                                <div style={{ fontWeight: 'bold' }}>{group.nomination || group.name}</div>
                                                {group.nomination && group.name && <div style={{ fontSize: '12px', color: '#666' }}>{group.name}</div>}
                                            </td>
                                            <td>{group.direction_name || '-'}</td>
                                            <td>{group.region_name || '-'}</td>
                                            {user?.role === 'admin' && <td>{group.manager_name || '-'}</td>}
                                            <td>{formatDateShortRu(group.earliest_event_date)}</td>
                                            <td>
                                                {[group.price_per_cycle, group.price_full_course].some(v => v != null && Number(v) > 0)
                                                    ? <div style={{ lineHeight: 1.4 }}>
                                                        {group.price_per_cycle > 0 && <><div>За цикл:</div><div>{Number(group.price_per_cycle).toLocaleString('ru-RU')} ₽</div></>}
                                                        {group.price_full_course > 0 && <><div>За курс:</div><div>{Number(group.price_full_course).toLocaleString('ru-RU')} ₽</div></>}
                                                    </div>
                                                    : '—'}
                                            </td>
                                            <td>
                                                <span className={`status-badge ${getStatusBadge(group.recruitment_status).className}`} style={{ fontSize: '12px', padding: '2px 8px' }}>
                                                    {getStatusBadge(group.recruitment_status).label}
                                                </span>
                                                {group.is_accepted && <span style={{ marginLeft: '5px' }}>✅</span>}
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                                    {user?.role === 'admin' && !group.is_accepted && (
                                                        <button className="btn-icon" onClick={() => handleApproveGroup(group.id)} title="Подтвердить">✔️</button>
                                                    )}
                                                    {user?.role === 'admin' && group.is_accepted && (
                                                        <button className="btn-icon" onClick={() => handleUnapproveGroup(group.id)} title="Снять подтверждение">❌</button>
                                                    )}
                                                    <button className="btn-icon" onClick={() => handleEditGroup(group)} title="Настроить">⚙️</button>
                                                    <button className="btn-icon" onClick={() => handleOpenSchedule(group)} title="Расписание">📅</button>
                                                    <button className="btn-icon" onClick={() => handleGroupApplications(group.id)} title="Заявки">👥</button>
                                                    <button className="btn-icon" onClick={() => handleViewPublicPage(group.id)} title="Публичная страница">🔗</button>
                                                    {!group.is_accepted && (
                                                        <button className="btn-icon" onClick={() => handleDeleteGroup(group.id, group.nomination)} title="Удалить">🗑️</button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Группы без расписания */}
                    {groupsWithoutSchedule.length > 0 && (
                        <div className="groups-section groups-without-schedule">
                            <h3 className="section-title">
                                <Calendar size={20} />
                                Группы без расписания ({groupsWithoutSchedule.length})
                                <span className="warning-text">⚠️ Требуют настройки расписания</span>
                            </h3>
                            <table className="compact-groups-table">
                                <thead>
                                    <tr>
                                        <th>Группа</th>
                                        <th>{directionTerms.one}</th>
                                        <th>Регион</th>
                                        {user?.role === 'admin' && <th>Менеджер</th>}
                                        <th>Стоимость</th>
                                        <th>Статус</th>
                                        <th>Действия</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {groupsWithoutSchedule.map(group => (
                                        <tr key={group.id} className={`${!group.is_accepted ? 'row-unconfirmed' : ''} row-no-schedule`}>
                                            <td>
                                                <div style={{ fontWeight: 'bold' }}>{group.nomination || group.name}</div>
                                                {group.nomination && group.name && <div style={{ fontSize: '12px', color: '#666' }}>{group.name}</div>}
                                            </td>
                                            <td>{group.direction_name || '-'}</td>
                                            <td>{group.region_name || '-'}</td>
                                            {user?.role === 'admin' && <td>{group.manager_name || '-'}</td>}
                                            <td>
                                                {[group.price_per_cycle, group.price_full_course].some(v => v != null && Number(v) > 0)
                                                    ? <div style={{ lineHeight: 1.4 }}>
                                                        {group.price_per_cycle > 0 && <><div>За цикл:</div><div>{Number(group.price_per_cycle).toLocaleString('ru-RU')} ₽</div></>}
                                                        {group.price_full_course > 0 && <><div>За курс:</div><div>{Number(group.price_full_course).toLocaleString('ru-RU')} ₽</div></>}
                                                    </div>
                                                    : '—'}
                                            </td>
                                            <td>
                                                <span className={`status-badge ${getStatusBadge(group.recruitment_status).className}`} style={{ fontSize: '12px', padding: '2px 8px' }}>
                                                    {getStatusBadge(group.recruitment_status).label}
                                                </span>
                                                {group.is_accepted && <span style={{ marginLeft: '5px' }}>✅</span>}
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                                    {user?.role === 'admin' && !group.is_accepted && (
                                                        <button className="btn-icon" onClick={() => handleApproveGroup(group.id)} title="Подтвердить">✔️</button>
                                                    )}
                                                    {user?.role === 'admin' && group.is_accepted && (
                                                        <button className="btn-icon" onClick={() => handleUnapproveGroup(group.id)} title="Снять подтверждение">❌</button>
                                                    )}
                                                    <button className="btn-icon" onClick={() => handleEditGroup(group)} title="Настроить">⚙️</button>
                                                    <button className="btn-icon" onClick={() => handleOpenSchedule(group)} title="Добавить расписание">➕📅</button>
                                                    <button className="btn-icon" onClick={() => handleGroupApplications(group.id)} title="Заявки">👥</button>
                                                    <button className="btn-icon" onClick={() => handleViewPublicPage(group.id)} title="Публичная страница">🔗</button>
                                                    {!group.is_accepted && (
                                                        <button className="btn-icon" onClick={() => handleDeleteGroup(group.id, group.nomination)} title="Удалить">🗑️</button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}

            <Modal
                isOpen={showGroupModal}
                onClose={() => setShowGroupModal(false)}
                title={editingGroup ? 'Редактировать группу' : 'Создать группу'}
                size="large"
            >
                <GroupForm
                    group={editingGroup}
                    initialTab={groupFormTab}
                    onSuccess={handleGroupSuccess}
                    onCancel={() => setShowGroupModal(false)}
                    onScheduleChange={refetch}
                />
            </Modal>
        </div>
    );
};

export default IndexPage;

